import dayjs from 'dayjs';

import DynamicDetails from './dynamicDetails';
import FixedDetails from './fixedDetails';
import SectionWrapper from './sectionWrapper';

import { DynamicFieldResponse } from '../../../../../interfaces/DynamicFieldResponse';
import { FinalReportData } from '../../../../../interfaces/FinalReportData';
import { FinalReportDocumentField } from '../../../../../interfaces/FinalReportDocumentField';
import { FixedFinalReportDetails } from '../../../../../interfaces/FixedFinalReportDetails';
import { GenericFinalReportDocument, ModifiedFinalReportDocument } from '../../../../../interfaces/ModifiedFinalReportDocument';

import { useReportStatusOptions } from '~/hooks/fetcher/useReportStatusOptions';

interface ModifiedFinalReportData extends GenericFinalReportDocument, FinalReportData { }

interface FinalReportSectionProps {
  data: ModifiedFinalReportData;
}

const FinalReportSection = ({ data }: FinalReportSectionProps) => {
  const { statusOptions } = useReportStatusOptions();
  const defaultFieldValues: FixedFinalReportDetails = {
    reportType: data.report_type,
    referenceID: data.report_reference,
    assignee: data.case_manager_name,
    priority: data.report_severity,
    dateReceived: data.date_received
      ? dayjs(data.date_received).format('YYYY-MM-DD')
      : null,
    reportNumber: data.report_number,
  };

  const customFieldValues: DynamicFieldResponse[] =
    data.final_report_custom_field_values.map((field) => {
      const siblingFields: DynamicFieldResponse[] = field.siblings.map(
        (sibling) => {
          return {
            id: sibling.id,
            placeholder: sibling.final_report_custom_field_sibling.placeholder,
            label: sibling.final_report_custom_field_sibling.label,
            dropdown_choices:
              sibling.final_report_custom_field_sibling.dropdown_choices,
            is_mandatory:
              sibling.final_report_custom_field_sibling.is_mandatory,
            type: sibling.final_report_custom_field_sibling.type,
            value: sibling.value,
          };
        },
      );

      return {
        id: field.id,
        placeholder: field.final_report_custom_field.placeholder,
        label: field.final_report_custom_field.label,
        dropdown_choices: field.final_report_custom_field.dropdown_choices,
        is_mandatory: field.final_report_custom_field.is_mandatory,
        type: field.final_report_custom_field.type,
        value: field.value,
        siblings: siblingFields,
      };
    });

  const pdfFields: FinalReportDocumentField[] =
    data.final_report_custom_field_values.map((field) => {
      const siblingFields: FinalReportDocumentField[] = field.siblings.map(
        (sibling) => {
          return {
            title: sibling.final_report_custom_field_sibling.label,
            type: sibling.final_report_custom_field_sibling.type,
            value: sibling.value || '',
          };
        },
      );

      return {
        title: field.final_report_custom_field.label,
        type: field.final_report_custom_field.type,
        value: field.value || '',
        siblings: siblingFields,
      };
    });

  const pdfData: ModifiedFinalReportDocument = {
    title: data.title,
    has_informed_individuals: data.has_informed_individuals,
    informed_individuals: data.informed_individuals,
    status:
      statusOptions.find((option) => option.value === data.report_status_integer)
        ?.label ?? 'Closed',
    reportType: data.report_type,
    referenceID: data.report_number,
    assignee: data.case_manager_name,
    priority: data.report_severity,
    dateReceived: data.date_received
      ? dayjs(data.date_received).format('DD MMM YYYY')
      : null,
    fieldValues: pdfFields,
    dateCompleted: dayjs(data.completed_date).format('DD MMM YYYY'),
    caseManagerReferenceId: data.case_manager_reference_id,
  };

  return (
    <SectionWrapper>
      <FixedDetails
        values={defaultFieldValues}
        title={data.title}
        documentData={pdfData}
        caseID={data.case_manager_reference_id}
      />
      <DynamicDetails customValues={customFieldValues} />
    </SectionWrapper>
  );
};

export default FinalReportSection;
